import React from "react";
import { Info } from "lucide-react";
import { ClientScheduleSlot } from "@/types/staffing/serviceRequest";
import { SectionHeader, FieldRow, FieldLabel, ErrorMsg } from "./FormPrimitives";
import { ClientSchedulePicker } from "./ClientSchedulePicker";

interface ClientScheduleSectionProps {
  clientSchedule: ClientScheduleSlot[];
  onClientScheduleChange: (slots: ClientScheduleSlot[]) => void;
  /** The picker stays locked until a client has been chosen in step 2. */
  clientSelected: boolean;
  /**
   * Add Provider saves to the staff member's schedule (one range per day);
   * otherwise the times replace the client's availability (several allowed).
   */
  isAddProvider: boolean;
  error?: string;
  disabled: boolean;
}

export function ClientScheduleSection({
  clientSchedule,
  onClientScheduleChange,
  clientSelected,
  isAddProvider,
  error,
  disabled,
}: ClientScheduleSectionProps) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <SectionHeader step={4} label="Client Schedule" />

      <FieldRow>
        <FieldLabel htmlFor="client-schedule" required>
          Weekly Availability
        </FieldLabel>
        <p className="text-xs text-muted-foreground mb-2">
          {isAddProvider
            ? "Select the days and enter one start and end time for each. The times are saved on the selected staff member's schedule for this client."
            : "Select the days the client is available and enter a start and end time for each. This replaces the client's saved availability when the request is submitted."}
        </p>

        {!clientSelected && (
          <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 mb-2">
            <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Select a client in step 2 before entering their schedule.
            </p>
          </div>
        )}

        <ClientSchedulePicker
          id="client-schedule"
          value={clientSchedule}
          onChange={onClientScheduleChange}
          disabled={disabled || !clientSelected}
          invalid={!!error}
          maxRangesPerDay={isAddProvider ? 1 : undefined}
        />
        <ErrorMsg msg={error} />
      </FieldRow>
    </div>
  );
}
